import * as React from "react";
import { Box, MenuItem, FormControlLabel, Radio, Checkbox } from "@mui/material";
import { getMasterReport } from "state/apis";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./index.css";
import Header from "components/Header";
import { TextField, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import moment from "moment";
import DefaultProperty from "components/DefaultProperty";
import { useSelector } from "react-redux";
import { formatAmount } from "utils/constants";

const MasterReport = () => {
    const propertyName = useSelector((state) => state.global.propertyName);
    const property = useSelector((state) => state.global.defaultProperty);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState({});
    const [openDefaultProperty, setOpenDefaultProperty] = React.useState(false);
    const [monthChecked, setMonthChecked] = React.useState(true);
    const [dateChecked, setDateChecked] = React.useState(false);
    const [onlyReceipts, setOnlyReceipts] = React.useState(false);

    const start = moment().startOf("month").format("YYYY-MM-DD");
    const end = moment().endOf("month").format("YYYY-MM-DD");
    const currentMonth = moment().format("YYYY-MM");

    const [filter, setFilter] = React.useState({
        startDate: start,
        endDate: end,
        monthValue: currentMonth,
        reportType: "Income and expense summary",
        // isNewContract: true,
    });

    React.useEffect(() => {
        if (!property) {
            setOpenDefaultProperty(true);
        } else {
            fetchData();
        }
    }, [filter.startDate, filter.endDate, monthChecked, property]);

    React.useEffect(() => {
        setIsLoading(true);
        if (!property) {
            setOpenDefaultProperty(true);
        } else {
            fetchData();
        }

        setIsLoading(false);
    }, []);

    const fetchData = async () => {
        try {
            let res = await getMasterReport({
                propertyId: property,
                startDate: filter.startDate,
                endDate: filter.endDate,
                // isNewContract: filter.isNewContract,
            });
            setData(res.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleMonthChange = (event) => {
        const isChecked = event.target.checked;
        setMonthChecked(isChecked);
        if (isChecked) {
            setDateChecked(false);
        }
    };

    const handleDateChange = (event) => {
        const isChecked = event.target.checked;
        setDateChecked(isChecked);
        if (isChecked) {
            setMonthChecked(false);
        }
    };

    const handleFrom = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: formattedDate,
        }));
    };
    const handleTo = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            endDate: formattedDate,
        }));
    };
    const handleMonth = (date) => {
        if (!date) return;
        const selectedDate = dayjs(date);

        const startDate = selectedDate.startOf("month").format("YYYY-MM-DD");
        const endDate = selectedDate.endOf("month").format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: startDate,
            endDate: endDate,
            monthValue: date ? date.format("YYYY-MM") : null,
        }));
    };

    const handleCloseDefaultProperty = () => {
        setOpenDefaultProperty(false);
    };

    const handleOnlyReceipts = (e) => {
        let value = e.target.checked;
        if (value) {
            setOnlyReceipts(true);
        } else {
            setOnlyReceipts(false);
        }
    };

    // const handleNewContract = (e) => {
    //     const value = e.target.checked;
    //     setFilter((prevData) => ({
    //         ...prevData,
    //         isNewContract: value,
    //     }));
    // };

    const reportRef = React.useRef();

    const downloadCSV = () => {
        // Define column headers
        const headers = [
            "SL NO",
            "UNIT",
            "CONTRACT FROM",
            "CONTRACT TO",
            "RENT",
            "CUMULATIVE AMOUNT",
            "UP TO",
            "DEPOSIT",
            "BALANCE",
            "DAYS",
            "DUE AMOUNT",
            "REMARK",
            "AMOUNT",
        ];

        // Map data to rows
        const rows = data?.data?.map((val, index) => {
            const isVacant = val.vacant === 0;
            return isVacant
                ? [
                      index + 1,
                      val.unitNumber,
                      val.contractFrom ? moment(val.contractFrom).format("DD/MM/YYYY") : "-",
                      val.contractTo ? moment(val.contractTo).format("DD/MM/YYYY") : "-",
                      formatAmount(Number(val.yearRent)),
                      formatAmount(Number(val.overallReceiptAmount)),
                      val.paidUntilDate || "-",
                      val.depositAmount ? formatAmount(val.depositAmount) : "-",
                      val.balanceAmount ? formatAmount(val.balanceAmount) : "-",
                      val.dayDiff || "-",
                      formatAmount(Number(val.dueAmount)),
                      val?.vacantLabel === "Yes" && val?.terminatedDate
                          ? moment(val.terminatedDate).format("DD/MM/YYYY")
                          : "-",
                      formatAmount(Number(val.unitExpenses)),
                  ]
                : [index + 1, val.unitNumber, "VACANT", "", "", "", "", "", "", "", "", "", ""];
        });

        // Add headers to rows
        const csvData = [headers, ...rows];

        // Convert to CSV string
        const csvContent = csvData
            .map((row) => row.map((cell) => `"${cell}"`).join(","))
            .join("\n");

        // Create Blob and download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", `report_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = () => {
        const input = reportRef.current;

        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const margin = 10; // Add margin (padding)

            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * (pdfWidth - 2 * margin)) / imgProps.width;

            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
            heightLeft -= pdfHeight - 2 * margin;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
                heightLeft -= pdfHeight - 2 * margin;
            }

            pdf.save(`${propertyName}-income-and-expense.pdf`);
        });
    };
    let totalOtherIncome = 0;
    return (
        <Box m="1.5rem 2.5rem" mb={5}>
            <Box display="flex" justifyContent="flex-end" pt={0} paddingLeft={0}>
                <Grid container spacing={1} p={"5px"} alignItems="center">
                    <Grid item xs={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={data.vacant}
                                    onChange={(e) => handleOnlyReceipts(e)}
                                    name="vacant"
                                    color="primary"
                                />
                            }
                            label="Only Receipts"
                        />
                    </Grid>
                    {/* <Grid item xs={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filter.isNewContract}
                                    onChange={handleNewContract}
                                    name="isNewContract"
                                    color="primary"
                                />
                            }
                            label="New Contract"
                        />
                    </Grid> */}
                    <Grid item>
                        <FormControlLabel
                            value="month"
                            control={
                                <Radio
                                    checked={monthChecked}
                                    onChange={handleMonthChange}
                                    color="primary"
                                />
                            }
                            label="Month"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            value="date"
                            control={
                                <Radio
                                    checked={dateChecked}
                                    onChange={handleDateChange}
                                    color="primary"
                                />
                            }
                            label="Date Wise"
                        />
                    </Grid>
                    {dateChecked && (
                        <>
                            <Grid item xs={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From"
                                        name="contractFrom"
                                        onChange={handleFrom}
                                        value={filter.startDate ? dayjs(filter.startDate) : null}
                                        format="DD/MM/YYYY"
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                "&.Mui-focused": {
                                                    color: "primary.main",
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To"
                                        name="contractTo"
                                        onChange={handleTo}
                                        value={filter.endDate ? dayjs(filter.endDate) : null}
                                        format="DD/MM/YYYY"
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                "&.Mui-focused": {
                                                    color: "primary.main",
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </>
                    )}
                    {monthChecked && (
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label={"Month"}
                                    views={["month", "year"]}
                                    name="month"
                                    onChange={handleMonth}
                                    value={filter.monthValue ? dayjs(filter.monthValue) : null}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: "primary.main",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            "&.Mui-focused": {
                                                color: "primary.main",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )}
                    <Grid item xs></Grid>
                    <Grid item xs="auto">
                        <button className="download-btn" onClick={handlePrint}>
                            Download PDF
                        </button>
                    </Grid>
                    {/* <Grid item xs="auto">
                        <button className="download-btn" onClick={downloadCSV}>
                            Download CSV
                        </button>
                    </Grid> */}
                </Grid>
            </Box>
            {!property && (
                <DefaultProperty
                    open={openDefaultProperty}
                    onClose={() => handleCloseDefaultProperty}
                />
            )}
            <div className="report-container">
                <div ref={reportRef}>
                    <table border="1" cellPadding="5" cellSpacing="0">
                        <thead>
                            <tr>
                                <th colSpan={12} style={{ textAlign: "center" }}>
                                    {propertyName}
                                </th>
                                <th colSpan={1} style={{ textAlign: "center" }}>
                                    {propertyName}
                                </th>
                            </tr>
                            <tr>
                                <th colSpan={12} style={{ textAlign: "center" }}>
                                    {monthChecked
                                        ? `DETAILS OF INCOME AND EXPENSE FOR THE MONTH OF  ${moment(
                                              filter.monthValue
                                          )
                                              .format("MMMM YYYY")
                                              .toUpperCase()}`
                                        : `DETAILS OF INCOME AND EXPENSE FOR THE PERIOD OF  ${filter.startDate} AND ${filter.endDate}`}
                                </th>
                                <th colSpan={1} style={{ textAlign: "center" }}>
                                    {monthChecked
                                        ? `Exps (${moment(filter.monthValue).format("MMM-YYYY")})`
                                        : `EXPENSE FOR TH PERIOD OF  ${filter.startDate} AND ${filter.endDate}`}
                                </th>
                            </tr>
                            <tr>
                                <th rowSpan={1} style={{ textAlign: "center" }}>
                                    SL NO
                                </th>
                                <th style={{ textAlign: "center" }}>UNIT</th>
                                <th colSpan={2} style={{ textAlign: "center" }}>
                                    CONTRACT PERIOD
                                </th>
                                <th></th>
                                <th colSpan={2} style={{ textAlign: "center" }}>
                                    RENT COLLECTED
                                </th>
                                <th style={{ textAlign: "center" }}>DEPOSIT</th>
                                <th style={{ textAlign: "center" }}>BAL</th>
                                <th style={{ textAlign: "center" }}>DAYS</th>
                                <th style={{ textAlign: "center" }}>DUE AMT</th>
                                <th style={{ textAlign: "center" }}>REMARK</th>
                                <th style={{ textAlign: "center" }}>AMOUNT</th>
                            </tr>
                            <tr>
                                <th></th>
                                <th></th>
                                <th style={{ textAlign: "center" }}>FROM</th>
                                <th style={{ textAlign: "center" }}>TO</th>
                                <th style={{ textAlign: "center" }}>RENT</th>
                                <th style={{ textAlign: "center" }}>CUMULATIVE AMOUNT</th>
                                <th style={{ textAlign: "center" }}>UP TO</th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {data && data.data ? (
                                <>
                                    {data.data
                                        .filter((val) => !onlyReceipts || val.receiptAmount > 0)
                                        .map((val, index) => {
                                            const isContractExpired =
                                                val.contractTo &&
                                                moment(val.contractTo).isBefore(
                                                    moment(filter.endDate)
                                                );
                                            const rowStyle = {
                                                backgroundColor: isContractExpired
                                                    ? "#ff8080"
                                                    : "transparent",
                                            };

                                            return val.vacant === 0 ? (
                                                <tr key={index} style={rowStyle}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        {val.unitNumber}
                                                        <br />
                                                        {val?.vacantLabel === "Yes" ? (
                                                            <span style={{ color: "red" }}>
                                                                Vacant
                                                            </span>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                    <td>
                                                        {val.contractFrom
                                                            ? moment(val.contractFrom).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        {val.contractTo
                                                            ? moment(val.contractTo).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.yearRent))}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(
                                                            Number(val.overallReceiptAmount)
                                                        )}
                                                    </td>
                                                    <td>{val.paidUntilDate}</td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {val.depositAmount
                                                            ? `${formatAmount(
                                                                  val.depositAmount
                                                              )}.00`
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {val.balanceAmount
                                                            ? `${formatAmount(val.balanceAmount)}`
                                                            : "-"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: "right",
                                                            color:
                                                                val.dayDiff < 0 ? "red" : "inherit",
                                                        }}
                                                    >
                                                        {val.dayDiff}
                                                    </td>

                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.dueAmount))}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        {val?.vacantLabel === "Yes" &&
                                                        val?.terminatedDate
                                                            ? moment(val.terminatedDate).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.unitExpenses))}
                                                    </td>
                                                </tr>
                                            ) : (
                                                <tr key={index} style={rowStyle}>
                                                    <td>{index + 1}</td>
                                                    <td>{val.unitNumber}</td>
                                                    <td colSpan={10}>VACANT</td>
                                                </tr>
                                            );
                                        })}

                                    {data?.futureReceipts?.length > 0 && (
                                        <>
                                            <tr>
                                                <td
                                                    colSpan="13"
                                                    style={{ textAlign: "center" }}
                                                ></td>
                                            </tr>
                                            <tr>
                                                <td colSpan="13" style={{ textAlign: "start" }}>
                                                    Additional Collections
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {data?.futureReceipts
                                        .filter((val) => !onlyReceipts || val.receiptAmount > 0)
                                        .map((val, index) => {
                                            const isContractExpired =
                                                val.contractTo &&
                                                moment(val.contractTo).isBefore(
                                                    moment(filter.endDate)
                                                );
                                            const rowStyle = {
                                                backgroundColor: isContractExpired
                                                    ? "#ff8080"
                                                    : "transparent",
                                            };

                                            return (
                                                <tr key={index} style={rowStyle}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        {val.unitNumber}
                                                        <br />
                                                        {val?.vacantLabel === "Yes" ? (
                                                            <span style={{ color: "red" }}>
                                                                Vacant
                                                            </span>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                    <td>
                                                        {val.contractFrom
                                                            ? moment(val.contractFrom).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        {val.contractTo
                                                            ? moment(val.contractTo).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.yearRent))}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.receiptAmount))}
                                                    </td>
                                                    <td>{val.paidUntilDate}</td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {val.depositAmount
                                                            ? `${formatAmount(
                                                                  val.depositAmount
                                                              )}.00`
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {val.balanceAmount
                                                            ? `${formatAmount(
                                                                  val.balanceAmount
                                                              )}.00`
                                                            : "-"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: "right",
                                                            color:
                                                                val.dayDiff < 0 ? "red" : "inherit",
                                                        }}
                                                    >
                                                        {val.dayDiff}
                                                    </td>

                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.dueAmount))}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        {val?.vacantLabel === "Yes" &&
                                                        val?.terminatedDate
                                                            ? moment(val.terminatedDate).format(
                                                                  "DD/MM/YYYY"
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.unitExpenses))}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                    <tr>
                                        <td></td>
                                        <td style={{ fontWeight: "bold" }}>Total</td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalRent))}
                                        </td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalCollectedRent))}
                                        </td>
                                        <td></td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalDeposit))}
                                        </td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalBalance))}
                                        </td>
                                        <td></td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalDueAmount))}
                                        </td>
                                        <td></td>
                                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {formatAmount(Number(data.totalUnitExpense))}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>Summary</td>
                                        <td colSpan={10}></td>
                                        <td>BUILDING EXPENSES</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={12} style={{ textAlign: "right" }}></td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatAmount(Number(data?.totalPropertyExpense))}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td colSpan={3}>TOTAL RENT COLLECTED</td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatAmount(Number(data.totalCollectedRent))}
                                        </td>
                                        <td colSpan={7}></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td colSpan={3}>TOTAL DEPOSIT</td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatAmount(Number(data.totalDeposit))}
                                        </td>
                                        <td colSpan={7}></td>
                                        <td></td>
                                    </tr>
                                    {data?.otherIncome.length > 0 &&
                                        data.otherIncome.map((val, index) => {
                                            totalOtherIncome += val.amount;
                                            return (
                                                <tr key={index}>
                                                    <td></td>
                                                    <td colSpan={3}>
                                                        OTHER INCOME ({val?.unit?.unitNumber})
                                                        {val.rentAdjustFromDeposit === true
                                                            ? "(Adjusted rent from deposit)"
                                                            : ""}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.amount))}
                                                    </td>
                                                    <td colSpan={7}>
                                                        {val?.remarks ? val.remarks : ""}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            );
                                        })}

                                    <tr>
                                        <td></td>
                                        <td colSpan={3}>TOTAL EXPENSE</td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatAmount(Number(data.totalOverAllExpense))}
                                        </td>
                                        <td colSpan={7}></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td colSpan={3}>NET INCOME</td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatAmount(
                                                Number(
                                                    data.totalCollectedRent +
                                                        totalOtherIncome +
                                                        data.totalDeposit -
                                                        data.totalOverAllExpense
                                                )
                                            )}
                                        </td>
                                        <td colSpan={7}></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td colSpan={13}></td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="13" style={{ textAlign: "center" }}>
                                        Loading...
                                    </td>
                                </tr>
                            )}
                        </tbody>

                        {/* </tbody> */}
                    </table>
                </div>
            </div>
        </Box>
    );
};
// };

export default MasterReport;
